<template>
    <div>
        <!-- Manage Menu -->
        <BottomSheetModal v-model="showManageMenu">
            <div class="manage-menu-content">
                <button
                    v-if="product.sales_type === 'fixed' || product.sales_type !== 'fixed' && product.paused"
                    class="manage-option" @click="handleEdit">
                    ✏️ {{ $t('action.update') }}
                </button>
                <button
                    v-if="product.sales_type === 'fixed' || product.sales_type !== 'fixed' && product.paused"
                    class="manage-option" :class="product.paused ? 'option-success' : 'option-warning'"
                    @click="handleTogglePause">
                    {{ product.paused ? '▶️ ' + $t('action.resume') : '⏸ ' + $t('action.pause') }}
                </button>
                <button v-if="product.sales_type === 'fixed'" class="manage-option option-danger"
                    @click="handleDelete">
                    🗑️ {{ $t('action.delete') }}
                </button>
            </div>
        </BottomSheetModal>

        <!-- Boost Plans Menu -->
        <BottomSheetModal 
            v-model="showBoostMenu" 
            :show-back-button="currentStep === 'payment'"
            :back-text="$t('action.back') || 'Retour'"
            sheet-class="boost-sheet"
            @back="backToPlans">
            
            <!-- Plans Step -->
            <div v-if="currentStep === 'plans'">
                <!-- Featured Plan Card -->
                <div class="featured-plan-card">
                    <div class="featured-badge">
                        <span class="mdi mdi-trophy"></span>
                    </div>
                    <div class="featured-content">
                        <h5>{{ $t('boost.visibleTopResults') }}</h5>
                    </div>
                </div>

                <!-- Plan Groups -->
                <div v-for="(groupPlans, groupType) in groupedPlans" :key="groupType" class="plan-group">
                    <div class="plan-group-title">{{ $t(`boosterPlans.type.${groupType}`) }}</div>

                    <div class="boost-plans-list">
                        <div v-for="plan in groupPlans" :key="plan.id" class="boost-plan-option"
                            :class="{ 'selected': selectedPlanIds[groupType] === plan.id }"
                            @click="selectPlan(groupType, plan.id)">
                            <div class="plan-radio">
                                <div class="radio-outer">
                                    <div v-if="selectedPlanIds[groupType] === plan.id" class="radio-inner">
                                    </div>
                                </div>
                            </div>
                            <div class="plan-info">
                                <div class="plan-duration">{{ plan.name }}</div>
                            </div>
                            <div class="plan-price">{{ formatPrice(plan.price) }}</div>
                        </div>
                    </div>

                    <!-- Description for selected plan in this group -->
                    <div v-if="selectedPlanIds[groupType]" class="plan-description-wrapper">
                        <div class="plan-description">
                            {{groupPlans.find(p => p.id === selectedPlanIds[groupType])?.description}}
                        </div>
                    </div>
                </div>

                <div v-if="plans.length === 0" class="empty-state">
                    {{ $t('sentence.noPlans') }}
                </div>

                <!-- Total and Action Button -->
                <div class="d-flex-center boost-footer">
                    <div class="boost-total">
                        <span class="total-price">{{ formatPrice(totalPrice) }}</span>
                    </div>
                    <button class="btn-validate" :disabled="!hasSelectedPlans" @click="proceedToPayment">
                        {{ $t('menu.next') }}
                    </button>
                </div>
            </div>

            <!-- Payment Step -->
            <div v-if="currentStep === 'payment'">
                <div class="payment-header">
                    <h5>{{ $t('payment.selectMethod') || 'Sélectionner le mode de paiement' }}</h5>
                </div>

                <!-- Loading State -->
                <div v-if="loadingPaymentMethods" class="loading-state">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>

                <!-- Payment Methods List -->
                <div v-else class="payment-methods-list">
                    <div v-for="method in paymentTypes" :key="method.id" class="payment-method-option"
                        :class="{ 'selected': selectedPaymentType === method.id }"
                        @click="selectPaymentMethod(method.id)">
                        <div class="payment-radio">
                            <div class="radio-outer">
                                <div v-if="selectedPaymentType === method.id" class="radio-inner"></div>
                            </div>
                        </div>
                        <div class="payment-info">
                            <div class="payment-name">{{ method.name }}</div>
                            <div v-if="method.description" class="payment-description">
                                {{ method.description }}
                            </div>
                        </div>
                    </div>

                    <div v-if="paymentTypes.length === 0" class="empty-state">
                        {{ $t('payment.noMethods') }}
                    </div>
                </div>

                <!-- Summary and Confirm -->
                <div class="payment-summary">
                    <div class="summary-row">
                        <span class="summary-label">{{ $t('boost.selectedPlans') || 'Plans sélectionnés' }}:</span>
                        <span class="summary-value">{{ selectedPlanCount }}</span>
                    </div>
                    <div class="summary-row total-row">
                        <span class="summary-label">{{ $t('ecommerce.total') }}:</span>
                        <span class="summary-value">{{ formatPrice(totalPrice) }}</span>
                    </div>
                </div>
                <div class="d-flex justify-content-end">
                    <button class="btn-validate" :disabled="!selectedPaymentType" @click="handleBoost">
                        {{ $t('action.validateAndPay') }}
                    </button>
                </div>
            </div>
        </BottomSheetModal>
    </div>
</template>

<script>
import helper from "@/services/helper";
import BottomSheetModal from './BottomSheetModal.vue';

export default {
    name: 'BoosterManagementModal',
    components: {
        BottomSheetModal
    },
    props: {
        product: {
            type: Object,
            required: true
        },
        plans: {
            type: Array,
            default: () => []
        },
        showManage: {
            type: Boolean,
            default: false
        },
        showBoost: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentStep: 'plans',
            selectedPlanIds: {},
            paymentTypes: [],
            selectedPaymentType: null,
            loadingPaymentMethods: false
        };
    },
    computed: {
        showManageMenu: {
            get() {
                return this.showManage;
            },
            set(value) {
                // Emit both kebab-case and camelCase update events so parent components
                // using either `:show-manage.sync` or `:showManage.sync` receive the update.
                this.$emit('update:show-manage', value);
                this.$emit('update:showManage', value);
            }
        },
        showBoostMenu: {
            get() {
                return this.showBoost;
            },
            set(value) {
                // Emit both kebab-case and camelCase update events so parent components
                // using either `:show-boost.sync` or `:showBoost.sync` receive the update.
                this.$emit('update:show-boost', value);
                this.$emit('update:showBoost', value);

                if (!value) {
                    this.resetBoostState();
                }
            }
        },
        groupedPlans() {
            const groups = {};
            this.plans.forEach(plan => {
                const type = plan.type || 'default';
                if (!groups[type]) {
                    groups[type] = [];
                }
                groups[type].push(plan);
            });
            return groups;
        },
        totalPrice() {
            let total = 0;
            Object.values(this.selectedPlanIds).forEach(planId => {
                const plan = this.plans.find(p => p.id === planId);
                if (plan) {
                    total += parseInt(plan.price);
                }
            });
            return total;
        },
        hasSelectedPlans() {
            return Object.keys(this.selectedPlanIds).length > 0;
        },
        selectedPlanCount() {
            return Object.keys(this.selectedPlanIds).length;
        }
    },
    watch: {
        showBoost(newVal) {
            if (newVal) {
                this.resetBoostState();
            }
        }
    },
    methods: {
        formatPrice: helper.formatPrice,

        resetBoostState() {
            this.currentStep = 'plans';
            this.selectedPlanIds = {};
            this.selectedPaymentType = null;
        },

        selectPlan(groupType, planId) {
            if (this.selectedPlanIds[groupType] === planId) {
                this.$delete(this.selectedPlanIds, groupType);
            } else {
                this.$set(this.selectedPlanIds, groupType, planId);
            }
        },

        async proceedToPayment() {
            if (!this.hasSelectedPlans) return;
            this.currentStep = 'payment';
            await this.getPaymentTypes();
        },

        backToPlans() {
            this.currentStep = 'plans';
            this.selectedPaymentType = null;
        },

        async getPaymentTypes() {
            this.loadingPaymentMethods = true;
            try {
                const response = await this.$axios.get("payment-methods");
                this.paymentTypes = Object.values(response.data.data);
            } catch (err) {
                console.error("Error loading payment methods:", err);
                this.$notify({
                    group: "error",
                    title: this.$t('error.title') || "Erreur",
                    type: "error",
                    text: this.$t('payment.loadError') || "Erreur lors du chargement des modes de paiement"
                });
            } finally {
                this.loadingPaymentMethods = false;
            }
        },

        selectPaymentMethod(methodId) {
            this.selectedPaymentType = methodId;
        },

        handleEdit() {
            this.showManageMenu = false;
            this.$emit('edit');
        },

        handleTogglePause() {
            this.showManageMenu = false;
            this.$emit('toggle-pause');
        },

        handleDelete() {
            this.showManageMenu = false;
            this.$emit('delete');
        },

        handleBoost() {
            if (!this.hasSelectedPlans || !this.selectedPaymentType) return;

            const payload = {
                planIds: Object.values(this.selectedPlanIds),
                paymentType: this.selectedPaymentType
            };

            this.showBoostMenu = false;
            this.$emit('boost', payload);
        }
    }
};
</script>

<style scoped>
.boost-sheet {
    max-height: 90vh;
    margin-bottom: 70px;
}

.payment-header {
    padding: 1rem;
    text-align: center;
}

.payment-header h5 {
    margin: 0;
    font-weight: 600;
    color: #333;
}

.loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;
}

.payment-methods-list {
    padding: 1rem 0;
}

.payment-method-option {
    display: flex;
    align-items: flex-start;
    padding: 1rem;
    margin-bottom: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.payment-method-option:hover {
    border-color: #ccc;
    background-color: #f8f9fa;
}

.payment-method-option.selected {
    border-color: #007bff;
    background-color: #e7f3ff;
}

.payment-radio {
    margin-right: 1rem;
    padding-top: 0.25rem;
}

.payment-info {
    flex: 1;
}

.payment-name {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
}

.payment-description {
    font-size: 0.875rem;
    color: #666;
}

.payment-summary {
    padding: 1rem;
    margin: 1rem 0;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
}

.summary-label {
    color: #666;
}

.summary-value {
    font-weight: 600;
    color: #333;
}

.total-row {
    border-top: 2px solid #dee2e6;
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    font-size: 1.1rem;
}

.total-row .summary-value {
    color: #007bff;
}

.empty-state {
    text-align: center;
    padding: 2rem;
    color: #999;
}

.plan-group {
    margin-bottom: 24px;
}

.plan-group-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
    padding: 8px 0;
    border-bottom: 2px solid #e0e0e0;
    text-transform: capitalize;
}

.plan-description-wrapper {
    margin-top: 8px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
}

.plan-description {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
}

.boost-footer {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 2px solid #e0e0e0;
}

.total-price {
    font-size: 20px;
    font-weight: bold;
    color: #2c3e50;
}

.manage-menu-content {
    padding: 0;
}

.manage-option {
    width: 100%;
    padding: 16px 20px;
    margin-bottom: 10px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background: var(--light);
    color: var(--color-3);
    transition: all 0.2s;
    text-align: left;
}

.manage-option:hover {
    background: var(--light);
    transform: translateX(5px);
}

.option-warning {
    background: var(--primary-light-200);
    color: var(--color-nz-300);
}

.option-warning:hover {
    background: var(--primary-light-100);
}

.option-success {
    background: var(--success-light);
    color: var(--success);
}

.option-success:hover {
    background: var(--success-light);
}

.option-danger {
    background: var(--danger-light);
    color: var(--color-oo-500);
}

.option-danger:hover {
    background: var(--danger-light);
    opacity: 0.9;
}

.featured-plan-card {
    background: linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%);
    border: 1px solid var(--gray-100);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 20px;
    position: relative;
}

.featured-badge {
    position: absolute;
    top: 20px;
    right: 12px;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--color-oo) 0%, var(--color-oo-400) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
}

.featured-badge .mdi {
    color: white;
    font-size: 24px;
}

.featured-content h5 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-2);
    margin: 0 0 8px 0;
}

.featured-content p {
    font-size: 13px;
    color: var(--color-6);
    line-height: 1.5;
    margin: 0 0 12px 0;
}

.boost-plans-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.boost-plan-option {
    background: white;
    border: 2px solid var(--gray-200);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.boost-plan-option:hover {
    border-color: var(--color-oo);
    background: var(--danger-light);
}

.boost-plan-option.selected {
    border-color: var(--color-oo);
    background: var(--danger-light);
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.15);
}

.plan-radio {
    flex-shrink: 0;
}

.radio-outer {
    width: 22px;
    height: 22px;
    border: 2px solid var(--gray-300);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.boost-plan-option.selected .radio-outer {
    border-color: var(--color-oo);
    border-width: 2px;
}

.radio-inner {
    width: 12px;
    height: 12px;
    background: var(--color-oo);
    border-radius: 50%;
}

.plan-info {
    flex: 1;
    padding-top: 1rem;
    padding-bottom: 1rem;
}

.plan-duration {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-2);
    margin-bottom: 2px;
}

.plan-price {
    font-size: 16px;
    font-weight: 700;
    color: var(--color-2);
}

.boost-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 4px;
}

.d-flex-center {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.d-flex {
    display: flex;
}

.justify-content-end {
    justify-content: flex-end;
}

.btn-validate {
    padding: 16px;
    background: linear-gradient(135deg, var(--color-oo) 0%, var(--color-oo-400) 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
}

.btn-validate:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(217, 119, 6, 0.4);
}

.btn-validate:active:not(:disabled) {
    transform: translateY(0);
}

.btn-validate:disabled {
    background: var(--gray-300);
    cursor: not-allowed;
    box-shadow: none;
}

@media (max-width: 576px) {
    .manage-option {
        padding: 14px 18px;
        font-size: 15px;
    }

    .featured-content h5 {
        font-size: 15px;
    }

    .featured-content p {
        font-size: 12px;
    }

    .boost-plan-option {
        padding: 14px;
    }

    .plan-duration {
        font-size: 14px;
    }

    .plan-price {
        font-size: 15px;
    }

    .total-price {
        font-size: 18px;
    }

    .btn-validate {
        padding: 14px;
        font-size: 15px;
    }
}
</style>